<template>
  <div class="centix-listinput">
      <span class="holder-span" :id="inputId">
        <input
          ref="input"
          type="text"
          enterkeyhint="next"
          class="form-control input-item"
          v-bind:class="{ filled : hasValue, required : showRequiredClass, disabled : !isEnabled }"
          v-model="searchKey"
          v-on:blur="onBlur($event)"
          v-on:focus="onFocus($event)"
          v-on:input="onInput()"
          v-on:keyup.down="moveDown($event)"
          v-on:keyup.enter="onReturn($event)"
          v-on:keydown.tab="onTab($event)"
          v-on:keyup.115="onShowAdvancedSearch()"
          v-on:keyup.up="moveUp($event)"
          v-on:keyup.esc="$event.target.blur()"
          v-bind:placeholder="placeholderValue"
          :disabled="!isEnabled">

        <a class="clear-button" v-if="hasValue && isEnabled && allowEmpty && !required" v-on:mousedown.prevent="clearForm()"></a>
        <loading-spinner v-if="isBusy" class="loading-spinner" v-bind:loading="isBusy"/>
        <error-popper v-if="hasError" class="error-popper" v-bind:message="lastError"/>
      </span>

      <div ref="hiddenholder" :class="{'hide' : !hasFocus}">
        <div class="result-holder" ref="popperdiv" v-show="hasFocus">
          <div :id="scrollerId" class="result-scroller custom-scroll">
            
            <!-- Result list -->
            <ul class="result-list" v-if="hasFocus">
              <li v-for="(result, index) in results" v-bind:key="index" :ref="'item'+index" v-bind:class="getListItemClass(index === selectedIndex, result)" class="listinput-item">
                <div v-on:mousedown.prevent="selectItem(index)">
                  <slot :result="result" :selected="index === selectedIndex">
                    <a href="javascript:void(0)" class="result-link">
                      <span class="id">{{ result.ID }}</span> {{ result.Descr }}
                    </a>
                  </slot>
                </div>
              </li>
            </ul>
            <!-- End result list -->

            <slot name="results" :results="results" :searchKey="searchKey"></slot>

            <div :id="listBottomId"></div>
          </div>

          <!-- Bottom bar -->
          <div class="row result-bottom no-gutters">
            <div class="col addcolumn" v-if="showCreateButton">
              <a  class="btn btn-light w-100 text-left" href="javascript:void(0)" v-on:mousedown="onShowCreateButton"><span class="icon add-icon"></span><span>{{ $t('listinput.create_new') }}</span></a>
            </div>
            <div class="col" v-if="showAdvancedSearch">
              <a class="btn btn-light w-100 text-left" href="javascript:void(0)" v-on:mousedown.prevent="onShowAdvancedSearch"><span class="icon search-icon"></span><span>{{$t('orderline.list_advanced_search')}}</span></a>
            </div>
            <div class="col pt-2" v-if="isArchiveList" v-on:mousedown.capture="onCheckArchive($event)">
              <span><input v-on:mousedown.prevent style="pointer-events:none" v-model="showArchived" type="checkbox" :for="_uid + 'archive'"><label :id="_uid + 'archive'">{{ $t('listinput.show_archived') }}</label></span>
            </div>
          </div>
        </div>
      </div>
  </div>
</template>
<script>
import lodashDebounce from 'lodash/debounce';
import LoadingSpinner from 'components/LoadingSpinner.vue';
import ErrorPopper from 'components/ErrorPopper.vue';
import { ListController } from 'controller/listcontroller.js';
import Popper from 'popper.js';
import utils from 'libs/utils.js';

export default {
  name: 'ListInput',
  props: {
    value: Number,
    listConfig: Object,
    isEnabled: {
      default: true
    },
    required: {
      default: false
    },
    hasScrollEvents: {
      default: false
    },
    addToBody: {
      default: false
    },
    allowEmpty: {},
    showAdvancedSearch: {
      default: false
    },
    showCreateButton: {
      default: false
    },
    placeholder: {
    },
    selectFirstItem: {
      default: true
    }
  },
  model: {
    prop: 'value',
    event: 'value-changed'
  },
  components: {
    'loading-spinner': LoadingSpinner,
    'error-popper': ErrorPopper
  },
  data () {
    return {
      searchKey: '',
      results: [],
      selectedIndex: 0,
      hasFocus: false,
      selectedKeyLocal: this.value,
      selectedItem: null,
      isBusy: false,
      hasError: false,
      lastError: '',
      popper: null,
      resultCount: 0,
      page: 0,
      pageSize: 30,
      showArchived: false,
      selectItemForSearch: null // used to select first item on "ENTER"
    };
  },
  mounted () {
    let self = this;
    let inputid = `#${this.inputId}`;

    this.popper = new Popper($(inputid), this.$refs.popperdiv, {
      modifiers: {
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport'
        }
      },
      placement: 'bottom-start'
    });

    if (!this.hasScrollEvents) this.popper.disableEventListeners();

    // adds to body for views with hidden overflows
    if (this.addToBody) $('body').append(this.$refs.popperdiv);

    $(`#${this.scrollerId}`).on('scroll', lodashDebounce(function () {
      let loadelem = document.getElementById(self.listBottomId);
      if (loadelem && utils.isElementInViewport(loadelem, document.getElementById(self.scrollerId))) {
        self.$log('load more');
        self.page++;
        self.getList(true);
      }
    }, 200));
  },
  beforeDestroy () {
    $(this.$refs.hiddenholder).append(this.$refs.popperdiv);
  },
  methods: {
    /**
     * Get list using configuration
     */
    getList: lodashDebounce(async function (loadmore = false) {
      let search = this.searchKey;
      // select first item if empty
      this.selectedIndex = (this.selectedItem !== null && this.searchKey.trim().length === 0) ? -1 : 0;
      
      if (this.selectFirstItem === false) {
        this.selectedIndex = -1;
      }

      const self = this;
      this.hasError = false;
      this.isBusy = true;

      if (!loadmore) self.page = 0;

      let controller = new ListController(this.$api, this.listConfig);
      controller.GetList(self.searchKey, self.page, self.pageSize, self.internalSettings).then(result => {
        if (loadmore) {
          self.results.push(...result);
        } else {
          self.results = result;
        }
        self.resultCount = self.results.length;
      }).catch(err => console.error('oops', err)).finally(() => {
        this.isBusy = false;
        this.$emit('getlist-done', { results: self.results, searchKey: self.searchKey });
        if (search && this.selectItemForSearch === search) {
          this.setKeyValue();
          this.onCancel();
          this.$refs.input.blur();
          this.selectItemForSearch = null;
        }
        this.$nextTick(() => {
          self.popper.scheduleUpdate();
        });
      });
    }, 150),
    /**
     * Get single item using configuration
     */
    getItem (autoid, external) {
      this.$log('getItem called');
      const self = this;
      this.hasError = false;
      this.isBusy = true;

      let controller = new ListController(this.$api, this.listConfig);
      controller.GetItem(autoid).then(result => {
        self.setItem(result, external);
      }).catch(err => this.setError(err)).finally(() => { this.isBusy = false; });
    },
    setError (error) {
      console.error(error);
      this.hasError = true;
      this.lastError = error.Error;
    },
    /**
     * Select item with mouse
     */
    selectItem (index) {
      this.selectedIndex = index;
      this.setKeyValue();
      this.$refs.input.blur();
      if (this.hasFocus) {
        this.hasFocus = false;
      }
    },
    /**
     * Select item using keyboard
     */
    onReturn (e) {
      const returnEvent = {
        searchKey: this.searchKey
      };

      if (this.isBusy) {
        this.selectItemForSearch = this.searchKey;
      } else {
        this.setKeyValue();
        this.onCancel();
        e.target.blur();
      }
      this.$emit('on-return', {
        ...returnEvent,
        itemSelected: !!this.selectedItem
      });
    },
    onBlur (e) {
      const returnEvent = {
        searchKey: this.searchKey,
        itemSelected: !!this.selectedItem
      };
        
      this.restoreOriginal();

      if (this.hasFocus) {
        this.$nextTick(() => {
          this.onCancel();
        });
      }
      this.$emit('on-blur', returnEvent);
    },
    onTab (e) {
      if (this.hasFocus) {
        e.preventDefault();
        this.onReturn(e);
      }
    },
    onFocus (e) {
      let timeout = (e.relatedTarget != null) ? 1000 : 0;
      let t = e.target;

      this.searchKey = '';
      setTimeout(() => {
        // recheck if item is the current active element
        if (document.activeElement === t) {
          this.continueFocus(e);
        }
      }, timeout);
      this.$emit('on-focus');
    },
    continueFocus (e) {
      this.getList();
      this.$nextTick(() => {
        this.hasFocus = true;
        this.popper.scheduleUpdate();
      });
    },
    onInput () {
      this.isBusy = true;
      this.getList();
    },
    onCancel () {
      this.hasFocus = false;
    },
    /**
     * Move down in results list
     */
    moveDown (e) {
      e.preventDefault();
      if (this.selectedIndex < this.results.length - 1) {
        this.selectedIndex++;
      }
    },
    /**
     * Move up in results list
     */
    moveUp (e) {
      e.preventDefault();
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    },
    setKeyValue: function () {
      if (this.selectedIndex >= 0) {
        var item = this.results[this.selectedIndex];
        this.setItem(item);
      } else {
        this.setItem(null);
      }
    },
    /**
     * Set selected "viewmodel" result item
     * @param {} item
     */
    setItem: function (item) {
      this.$log('setItem called diff', item);
      this.selectedItem = item;
      if (item == null) {
        this.selectedKeyLocal = null;
        this.searchKey = '';
      } else {
        this.selectedKeyLocal = item.AutoID;
        this.searchKey = this.getIDDescr(item);
      }
      this.$emit('item-changed', item);
      this.$emit('value-changed', this.selectedKeyLocal);
    },
    clearForm: function () {
      this.setItem(null);
    },
    restoreOriginal () {
      this.searchKey = this.getIDDescr(this.selectedItem);
    },
    getIDDescr (item) {
      if (item) {
        return (
          `${item.ID ? item.ID : ''}${item.Descr && item.ID ? ' - ' : ''}` +
          `${item.Descr ? item.Descr : ''}`
        );
      }  else {
        return '';
      }
    },
    focus() {
      this.$refs.input.focus();
      this.$refs.input.select();
    },
    onShowAdvancedSearch() {
      this.$emit('show-advanced-search', this.searchKey);
    },
    onCheckArchive(e) {
      e.preventDefault();
      this.showArchived = !this.showArchived;
      console.log('on check archive');
      this.$emit('show-archived', !this.showArchived);
      this.getList();
    },
    getListItemClass (isSelected, resultItem) {
      let result = [];
      if (isSelected) {
         result.push('selected');
      }
      if (resultItem.Archive && this.isArchiveList) {
        result.push('archive-item');
      }
      return result;
    },
    onShowCreateButton() {
      this.$emit('show-create-button', this.searchKey);
    },
    setSearchKey(val){
      this.searchKey = val;
    }
  },
  watch: {
    value: function (newvalue, oldvalue) {
      if (newvalue !== this.selectedKeyLocal) {
        if (newvalue == null && this.selectedKeyLocal == null) return; // https://codeburst.io/javascript-null-vs-undefined-20f955215a2
        if (newvalue != null) {
          this.getItem(newvalue, true);
        } else {
           this.clearForm();
        }
      }
    }
  },
  computed: {
    hasValue: function () {
      return this.selectedKeyLocal != null && this.selectedKeyLocal > 0;
    },
    placeholderValue: function () {
      if (this.selectedItem != null) {
        return this.getIDDescr(this.selectedItem);
      } else if (this.placeholder) {
        return this.placeholder;
      } else return this.$t('placeholder_search');
    },
    showRequiredClass: function () {
      if (this.required && !this.hasValue) {
        return true;
      } else {
        return false;
      }
    },
    inputId () {
      return this._uid + 'inp';
    },
    listBottomId () {
      return this._uid + 'bottom';
    },
    scrollerId () {
      return this._uid + 'scroller';
    },
    /** Internal settings that are sent when calling GetList on the listconfig as extra parameters */
    internalSettings() {
      let res = {};
      if (this.isArchiveList) {
        res.Archive = this.showArchived;
      }
      return res;
    },
    isArchiveList () {
      return (this.listConfig && this.listConfig.isArchiveList);
    }
  }
};

</script>
<style lang="scss" scoped>
@import 'styles/settings.scss';
.addcolumn{
  max-width: 150px;
}
.centix-listinput {
  display: inline-block;
  width: 100%;
  &:not(.disabled) {
    .input-item {
      &.required {
        border: 1px solid $error;
      }
      &.filled {
        padding-right: 25px;
        border: 1px solid #80bdff;
      }
    }
  }
  &.disabled {
    input {
      pointer-events: none;
      background: #e9ecef;
    }
  }
  &:hover {
    .clear-button {
      display: block;
    }
  }
  .input-item {
    width: 100%;
  }
  span.holder-span {
    position: relative;
    display: block;
    width: 100%;
  }
  .clear-button {
    position: absolute;
    right: 0;
    top: 0;
    background: url('~@/assets/remove.png') transparent no-repeat center right 16px;
    background-size: 12px 12px;
    width: 50px;
    height: 100%;
    cursor: pointer;
    display: none;
  }
  .loading-spinner, .error-popper {
    position: absolute;
    right: 4px;
    top: 4px;
    background: #fff;
  }
  .model-state-error {
    color: $error;
  }
}
/**  STYLE 3 */
.custom-scroll::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
  background-color: #F5F5F5;
}
.custom-scroll::-webkit-scrollbar {
  width: 6px;
  background-color: #F5F5F5;
}
.custom-scroll::-webkit-scrollbar-thumb {
  background-color: $blue300;
}
.result-holder {
  border: 1px solid rgba(0,0,0,.125);
  border-radius: 4px;
  padding: 6px;
  z-index: 500;
  background: #f2f4f6;
  .result-header, .result-bottom {
    .btn {
      color: #444;
      padding: 4px;
    }
    .icon {
      height: 12px;
      background-size: 12px;
    }
  }
  .result-scroller {
    max-height: 250px;
    overflow-y: scroll;
    .result-list {
      list-style: none;
      min-width: 300px;
      margin: 0;
      padding: 0;
      box-shadow: 0px 2px 5px 0px rgba(50, 50, 50, 0.25);
      li {
        margin: 0;
        padding: 0;
        a {
          padding: 8px;
          font-weight: normal;
          font-size: 12px;
          width: 100%;
          display: block;
          background-color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          background-size: 100%;
          span.id {
            font-weight: bold;
            // display: inline-block;
            // padding: 4px;
            // background: #efefef;
            // color: black;
            // font-weight: bold;
            // border-radius: 4px;
          }
        }
        &:hover a {
          background-color: $blue100;
        }
        &.selected a {
          background-color: #007bff;
          color: white;
        }
        &.archive-item a {
          background-color: #efefef;
          color: #999;
        }
      }
    }
  }
}
</style>
